/**
 * @file    useTableLayout
 * @scope   SHARED
 * @summary Keeps the line chart sized to the table area and tracks whether scrolling applies.
 * @consumes Table container ref, ECharts ref
 * @produces { lineChartHeight, showScroll }
 * @sideEffects window resize listener; ECharts instance resize.
 */
import { RefObject, useEffect, useState } from 'react';

const MIN_CHART_HEIGHT = 280;
const DEFAULT_CHART_HEIGHT = 360;

export const useTableLayout = (
  tableRef: RefObject<HTMLDivElement>,
  echartRef: RefObject<any>,
  showLineChart: boolean,
  data: any
) => {
  const [showScroll, setShowScroll] = useState<boolean>(false);
  const [lineChartHeight, setLineChartHeight] = useState<number>(DEFAULT_CHART_HEIGHT);

  useEffect(() => {
    if (showLineChart && echartRef.current) {
      setLineChartHeight(
        Math.max(MIN_CHART_HEIGHT, tableRef.current?.clientHeight ?? DEFAULT_CHART_HEIGHT)
      );
      setTimeout(() => {
        echartRef.current?.getEchartsInstance?.()?.resize();
      }, 0);
    }
  }, [showLineChart, data]);

  useEffect(() => {
    const handleResize = () => {
      if (tableRef.current) {
        setShowScroll(true);
        setLineChartHeight(Math.max(MIN_CHART_HEIGHT, tableRef.current.clientHeight));
      } else {
        setShowScroll(false);
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return { lineChartHeight, showScroll };
};
